// ---------------------------------------------------------------------------
// Bell Operations Console — pure helpers
// ---------------------------------------------------------------------------
// Date maths, deterministic RNG (so a refresh doesn't reshuffle demo history),
// i18n formatting and week roll-ups. No React, no DOM.
// ---------------------------------------------------------------------------
import {CONFIG, DAYS_EN, DAYS_NE, MON_EN, MON_NE, NEDIG, T} from "../constants/BellStatusConstants";

/* ── dates ──────────────────────────────────────────────────────────────── */
export const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const parseISO = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
};
export function weekStart(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - x.getDay()); // Sunday
    return x;
}
export function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}
export const todayISO = () => iso(new Date());
export function minutes(hhmm) {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
}
export const nowMinutes = () => {
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
};

// Convert a display time ("10:15 AM" / "01:30 PM" / "13:30") into 24h "HH:MM"
// which the grid's minutes()/formatting expects. Returns null when unparseable.
export function to24h(value) {
    const m = String(value || "").trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (!m) return null;
    let h = Number(m[1]);
    const min = Number(m[2]);
    const ap = m[3] ? m[3].toUpperCase() : null;
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    if (h > 23 || min > 59) return null;
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

const fmtHM = (date) =>
    `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

// Map a raw backend bell-log status onto the grid's status vocabulary.
export function normalizeLogStatus(raw) {
    const v = String(raw || "").toUpperCase();
    if (v.includes("FAIL") || v.includes("ERROR")) return "failed";
    if (v.includes("LATE")) return "late";
    if (v.includes("OFFLINE")) return "offline";
    if (v.includes("PEND") || v.includes("QUEUE")) return "pending";
    return "played";
}

// A bell-log entry belongs to a period when it shares the bell id, or failing
// that the same scheduled clock time.
function logMatchesPeriod(log, period) {
    if (log.bellId != null && period.bellId != null && String(log.bellId) === String(period.bellId)) {
        return true;
    }
    const logTime = to24h(log.hourTime || log.scheduledTime);
    return !!logTime && logTime === period.time;
}

// Build the {school, periods, days} structure the grid consumes purely from
// real data: the school's periods (bells) and the bell-log events for the week.
// No synthesised/mock statuses — a slot is coloured only by what the log says.
//   • matched log            → its status (played/late/failed/offline)
//   • no log, future slot    → pending (scheduled, not yet due)
//   • no log, past slot       → failed (missed — nothing was recorded)
export function buildWeek(school, periods, logs, from, to) {
    const safePeriods = Array.isArray(periods) ? periods : [];
    if (!safePeriods.length) {
        return {school, periods: [], days: []};
    }

    const nowKey = todayISO();
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    // Bucket logs by the calendar day they were played on.
    const logsByDate = {};
    (Array.isArray(logs) ? logs : []).forEach((log) => {
        const when = log.playedAt || log.createdAt || log.timestamp;
        if (!when) return;
        const d = new Date(when);
        if (Number.isNaN(d.getTime())) return;
        const key = iso(d);
        (logsByDate[key] = logsByDate[key] || []).push({...log, _when: d});
    });

    const days = [];
    let cur = parseISO(from);
    const end = parseISO(to);
    while (cur <= end) {
        const key = iso(cur);
        const dayLogs = logsByDate[key] || [];

        const events = safePeriods.map((p) => {
            const pm = minutes(p.time);
            const match = dayLogs.find((log) => logMatchesPeriod(log, p));

            if (match) {
                let status = normalizeLogStatus(match.status);
                const scheduled = new Date(cur);
                scheduled.setHours(Math.floor(pm / 60), pm % 60, 0, 0);
                const delay = Math.max(0, Math.round((match._when.getTime() - scheduled.getTime()) / 1000));
                if (status === "played" && delay >= CONFIG.LATE_THRESHOLD_SEC) status = "late";
                const rang = status === "played" || status === "late";
                return {
                    period_id: p.id,
                    status,
                    scheduled_at: p.time,
                    actual_at: rang ? fmtHM(match._when) : null,
                    delay_sec: rang ? delay : null,
                    strikes_played: rang ? p.strikes : 0,
                    device_online: status !== "offline",
                    source: match.source || null,
                    bell_music_url: match.bellMusicUrl || null,
                    error_code: status === "failed" ? match.errorCode || "PLAY_FAILED" : null,
                    error_message: status === "failed" ? match.errorMessage || null : null,
                };
            }

            const isFuture = key > nowKey || (key === nowKey && pm > nowMin + 2);
            if (isFuture) {
                return {
                    period_id: p.id, status: "pending", scheduled_at: p.time, actual_at: null,
                    delay_sec: null, strikes_played: 0, device_online: true,
                };
            }
            return {
                period_id: p.id, status: "failed", scheduled_at: p.time, actual_at: null,
                delay_sec: null, strikes_played: 0, device_online: true,
                error_code: "NO_LOG", error_message: "No play was recorded for this bell.",
            };
        });

        days.push({date: key, holiday: false, holiday_name: null, events});
        cur = addDays(cur, 1);
    }

    return {school, periods: safePeriods, days};
}

/* ── deterministic pseudo-random ───────────────────────────────────────── */
export function hash(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
export function rng(seed) {
    let a = hash(seed);
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/* ── i18n ───────────────────────────────────────────────────────────────── */
export function makeI18n(lang) {
    const t = (k) => (T[lang] && T[lang][k]) || (T.en[k] ?? k);
    const num = (s) => (lang === "ne" ? String(s).replace(/\d/g, (d) => NEDIG[d]) : String(s));
    const mon = (i) => (lang === "ne" ? MON_NE : MON_EN)[i];
    const days = lang === "ne" ? DAYS_NE : DAYS_EN;
    return {lang, t, num, mon, days};
}
export function fmtRange(a, b, i18n) {
    const A = i18n.mon(a.getMonth()), B = i18n.mon(b.getMonth());
    return A === B
        ? `${i18n.num(a.getDate())}–${i18n.num(b.getDate())} ${B} ${i18n.num(b.getFullYear())}`
        : `${i18n.num(a.getDate())} ${A} – ${i18n.num(b.getDate())} ${B} ${i18n.num(b.getFullYear())}`;
}

/* ── status → colours + label ──────────────────────────────────────────── */
export function statusMeta(st, t) {
    const map = {
        played: {label: t("played"), color: "var(--emerald)", bg: "var(--emerald-soft)", ink: "#04603C"},
        late: {label: t("late"), color: "var(--gold)", bg: "var(--gold-soft)", ink: "#7A5600"},
        failed: {label: t("failed"), color: "var(--fail)", bg: "var(--fail-soft)", ink: "#8A1D12"},
        offline: {label: t("offline"), color: "var(--offline)", bg: "var(--offline-soft)", ink: "#3B4759"},
        pending: {label: t("pending"), color: "#C9D3E2", bg: "#F7F9FC", ink: "#5C6B82"},
        holiday: {label: t("holiday"), color: "var(--gold)", bg: "#FBFAF6", ink: "#7A5600"},
        skipped: {label: t("skipped"), color: "var(--line)", bg: "#fff", ink: "var(--muted)"},
    };
    return map[st] || {label: st, color: "var(--line)", bg: "#fff", ink: "var(--muted)"};
}

/* ── week roll-up ──────────────────────────────────────────────────────── */
export function weekStats(data) {
    const all = [];
    (data.days || []).forEach((d) => (d.events || []).forEach((e) => all.push(e)));
    const c = (n) => all.filter((e) => e.status === n).length;
    const done = c("played") + c("late"), failed = c("failed"), offline = c("offline");
    const attempted = done + failed + offline;
    return {
        total: all.length - c("pending"),
        scheduled: all.length,
        played: c("played"),
        late: c("late"),
        failed,
        offline,
        attempted,
        rate: attempted ? Math.round((done / attempted) * 1000) / 10 : null,
    };
}

// Per-day success ratio, used for the header day-bars and the KPI sparkline.
export function perDayStats(data) {
    return (data.days || []).map((d) => {
        const ev = (d.events || []).filter((e) => e.status !== "pending");
        const ok = ev.filter((e) => e.status === "played" || e.status === "late").length;
        const bad = ev.filter((e) => e.status === "failed").length;
        const off = ev.filter((e) => e.status === "offline").length;
        return {pct: ev.length ? ok / ev.length : null, ok, bad, off, count: ev.length, holiday: d.holiday};
    });
}
